import React, { useState, useEffect, useMemo } from "react";
import { DashboardLayout } from "src/components/dashboard";
import { useNavigate, useParams } from "react-router-dom";
import PartnerBasicInfoForm from "src/components/partners/PartnerBasicInfoForm";
import PartnerFormHeader from "src/components/partners/PartnerFormHeader";
import PartnerFormNavigation from "src/components/partners/PartnerFormNavigation";
import {
  useGetPartnerProfileQuery,
  useGetCountryDetailsQuery,
  useUpdatePartnerProfileMutation,
  useUploadPartnerGalleryImageMutation,
  useAddOperatingHoursPartnerMutation,
} from "src/store/services/partnerApi";
import { toast, ToastContainer } from "react-toastify";

// Extended form data type to include all UI fields
type PartnerFormData = {
  businessName: string;
  email: string;
  phoneNumber: string;
  phoneCountryCode: string;
  yearsOfOperation: string;
  physicalAddress: string;
  city: string;
  stateProvince: string;
  zipPostalCode: string;
  country: string;
  businessHoursOpen: string;
  businessHoursClose: string;
  isRegistered: string;
  rcNumber: string;
  isOwner: string;
  designation: string;
  businessBio: string;
  logo: File | null;
  cover: File | null;
  gallery: File[];
  businessAccountManagerFirstName: string;
  businessAccountManagerLastName: string;
  businessAccountManagerPhoneNumber: string;
  businessAccountManagerPhoneCountryCode: string;
  businessAccountManagerGender: string;
  businessAccountManagerDesignation: string;
  accountManagerSourceBy: string;
  accountManagerPartnershipManager: string;
  serviceFeeMin: string;
  serviceFeeMax: string;
  latitude: string;
  longitude: string;
  can_deliver: string;
  businessAccountOfficerPhoneNumber: string;
  businessAccountOfficerPhoneCountryCode: string;
};

const EditPartner = () => {
  const navigate = useNavigate();
  const { partnerId } = useParams<{ partnerId: string }>();
  const { data: partnerProfile, isLoading: isLoadingProfile } =
    useGetPartnerProfileQuery(partnerId as string);
  const { data: countryDetails } = useGetCountryDetailsQuery();
  const [updatePartner, { isLoading }] = useUpdatePartnerProfileMutation();
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const [addPartnerHours, { isLoading: isdloading }] =
    useAddOperatingHoursPartnerMutation();
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ] as const;

  type Day = (typeof days)[number];

  type OperationalHours = {
    [key in Day]: {
      is_open: boolean;
      open: string;
      close: string;
    };
  };

  const [opeFormData, setOpeFormData] = useState<{
    operational_hours: OperationalHours;
  }>({
    operational_hours: {
      monday: { is_open: true, open: "08:00", close: "18:00" },
      tuesday: { is_open: true, open: "08:00", close: "18:00" },
      wednesday: { is_open: true, open: "08:00", close: "18:00" },
      thursday: { is_open: true, open: "08:00", close: "18:00" },
      friday: { is_open: true, open: "08:00", close: "16:00" },
      saturday: { is_open: true, open: "10:00", close: "14:00" },
      sunday: { is_open: false, open: "", close: "" },
    },
  });

  const handleOperationalHoursChange = (
    day: Day,
    field: "is_open" | "open" | "close",
    value: string | boolean,
  ) => {
    setOpeFormData((prev) => ({
      ...prev,
      operational_hours: {
        ...prev.operational_hours,
        [day]: {
          ...prev.operational_hours[day],
          [field]: value,
        },
      },
    }));
  };

  const [previewUrls, setPreviewUrls] = useState<{ file: File; url: string }[]>(
    [],
  );

  const [uploadGalleryImage, { isLoading: isUploading }] =
    useUploadPartnerGalleryImageMutation();

  const [formData, setFormData] = useState<PartnerFormData>({
    businessName: "",
    email: "",
    phoneNumber: "",
    phoneCountryCode: "",
    yearsOfOperation: "",
    physicalAddress: "",
    city: "",
    stateProvince: "",
    zipPostalCode: "",
    country: "",
    businessHoursOpen: "",
    businessHoursClose: "",
    isRegistered: "",
    rcNumber: "",
    isOwner: "",
    designation: "",
    businessBio: "",
    logo: null,
    cover: null,
    gallery: [],
    businessAccountManagerFirstName: "",
    businessAccountManagerLastName: "",
    businessAccountManagerPhoneNumber: "",
    businessAccountManagerPhoneCountryCode: "",
    businessAccountManagerGender: "",
    businessAccountManagerDesignation: "",
    accountManagerSourceBy: "",
    accountManagerPartnershipManager: "",
    serviceFeeMin: "",
    serviceFeeMax: "",
    latitude: "",
    longitude: "",
    can_deliver: "",
    businessAccountOfficerPhoneNumber: "",
    businessAccountOfficerPhoneCountryCode: "",
  });

  // Extract phone number and country code from full phone number
  const extractPhoneNumber = (phone: string) => {
    if (!phone) return { number: "", code: "" };

    // Remove all formatting (spaces, dashes, parentheses, etc.) except digits and +
    const cleaned = phone.replace(/[^\d+]/g, "");

    // Check for specific known country codes first (to avoid false matches)
    // Nigerian numbers: +234
    if (cleaned.startsWith("+234")) {
      let number = cleaned.substring(4); // Remove "+234"
      // Remove leading 0 if present (e.g., +23409063208267 -> 9063208267)
      if (number.startsWith("0")) {
        number = number.substring(1);
      }
      return { code: "+234", number: number };
    }

    // If no + prefix, check if it starts with country code without +
    // Handle Nigerian numbers: 2349063208267 or 23409063208267
    if (cleaned.startsWith("234") && cleaned.length >= 6) {
      const number = cleaned.substring(3);
      // Remove leading 0 if present (Nigerian numbers often have 0 after country code)
      const cleanNumber = number.startsWith("0") ? number.substring(1) : number;
      return { code: "+234", number: cleanNumber };
    }

    // Handle numbers that start with 0 (local format, assume Nigerian)
    if (cleaned.startsWith("0") && cleaned.length >= 10) {
      const number = cleaned.substring(1); // Remove leading 0
      return { code: "+234", number: number };
    }

    // Try to extract other country codes (usually 1-3 digits, some are 4)
    // Use a more restrictive pattern that avoids matching too many digits
    const matchWithPlus = cleaned.match(/^(\+\d{1,3})(\d{7,})$/); // At least 7 digits after country code
    if (matchWithPlus) {
      const countryCode = matchWithPlus[1];
      const number = matchWithPlus[2];
      return { code: countryCode, number: number };
    }

    // If no country code detected, return cleaned number without country code
    return { number: cleaned.replace(/\+/g, ""), code: "" };
  };

  // Pre-populate form data when profile is loaded
  useEffect(() => {
    if (partnerProfile?.payload) {
      const profile = partnerProfile.payload;
      const partnerInfo = profile.partner_info || {};
      const accOfficerInfo = profile.account_manager || {};
      const address = profile.address || {};
      const meta = profile.meta || {};
      const accountManager = profile.account_manager || {};

      // Extract phone number components
      const phoneData = extractPhoneNumber(profile.phone || "");

      // Extract contact person phone number
      const contactPersonPhoneData = extractPhoneNumber(
        partnerInfo.contact_person || "",
      );

      // Extract contact person phone number
      const AccountOfficerPhoneData = extractPhoneNumber(
        accOfficerInfo.account_manager_phone || "",
      );

      // Get country name from code/name mapping
      const countryName =
        countryDetails?.payload?.data?.find(
          (c) => c.code === profile.country || c.name === profile.country,
        )?.name ||
        profile.country ||
        "";

      // Extract country code for phone
      // Prefer the extracted code from the phone number, fallback to country dial code
      const phoneCountryCode =
        phoneData.code ||
        countryDetails?.payload?.data?.find(
          (c) => c.code === profile.country || c.name === profile.country,
        )?.dialCode ||
        "";

      // Extract country code for contact person phone
      const contactPersonPhoneCountryCode =
        contactPersonPhoneData.code ||
        countryDetails?.payload?.data?.find(
          (c) => c.code === profile.country || c.name === profile.country,
        )?.dialCode ||
        phoneCountryCode ||
        "";
      // Extract country code for contact person phone
      const AccountOfficerPhoneCountryCode =
        AccountOfficerPhoneData.code ||
        countryDetails?.payload?.data?.find(
          (c) => c.code === profile.country || c.name === profile.country,
        )?.dialCode ||
        phoneCountryCode ||
        "";

      const decodedBio = meta.bio
        ? decodeURIComponent(meta.bio)
        : partnerInfo.bio || "";

      const firstNameValue = profile.first_name || "";
      const lastNameValue = profile.last_name || "";
      console.log(opeFormData)
      console.log(profile?.operational_hours)
      setOpeFormData({
        operational_hours: profile?.operational_hours || {
      monday: { is_open: true, open: "08:00", close: "18:00" },
      tuesday: { is_open: true, open: "14:00", close: "18:00" },
      wednesday: { is_open: true, open: "08:00", close: "18:00" },
      thursday: { is_open: true, open: "08:00", close: "18:00" },
      friday: { is_open: true, open: "08:00", close: "16:00" },
      saturday: { is_open: false, open: "10:00", close: "14:00" },
      sunday: { is_open: false, open: "", close: "" },
    },
      });
      console.log(opeFormData)

      setFormData({
        businessName: profile.business_name || "",
        email: profile.email || "",
        phoneNumber: phoneData.number || "",
        phoneCountryCode: phoneCountryCode,
        yearsOfOperation: partnerInfo.years_of_operation?.toString() || "",
        physicalAddress:
          partnerInfo.partner_address || address.house_address || "",
        city: partnerInfo.city || address.city || "",
        stateProvince: partnerInfo.state || address.state || "",
        zipPostalCode: partnerInfo.zip_code || "",
        country: countryName,
        businessHoursOpen: partnerInfo.business_hours_open || "08:00",
        businessHoursClose: partnerInfo.business_hours_close || "17:00",
        can_deliver: partnerInfo.can_deliver ? "yes" : "no",
        isRegistered: partnerInfo.is_registered ? "yes" : "no",
        rcNumber: partnerInfo.rc_number || "",
        isOwner: partnerInfo.is_owner ? "yes" : "no",
        designation: profile.designation || "",
        businessBio: decodedBio || "",
        logo: null,
        cover: null,
        gallery: [],
        businessAccountManagerFirstName: firstNameValue,
        businessAccountManagerLastName: lastNameValue,
        businessAccountManagerPhoneNumber: contactPersonPhoneData.number || "",
        businessAccountManagerPhoneCountryCode: contactPersonPhoneCountryCode,
        businessAccountManagerGender: profile.gender || "",
        businessAccountManagerDesignation: profile.designation || "",
        accountManagerSourceBy: accountManager.source_by || "",
        accountManagerPartnershipManager:
          accountManager.partnership_manager || "",
        serviceFeeMin: profile.service_fee?.min
          ? String(profile.service_fee.min)
          : "",
        serviceFeeMax: profile.service_fee?.max
          ? String(profile.service_fee.max)
          : "",
        latitude: "",
        longitude: "",
        businessAccountOfficerPhoneNumber: AccountOfficerPhoneData.number || "",
        businessAccountOfficerPhoneCountryCode: AccountOfficerPhoneCountryCode,

      });
    }
  }, [partnerProfile, countryDetails]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (file: File) => {
    setFormData((prev) => ({ ...prev, logo: file }));
  };

  const handleCoverUpload = (file: File) => {
    setFormData((prev) => ({ ...prev, cover: file }));
  };

  // Handle place selection to update location coordinates
  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    if (place.geometry?.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setFormData((prev) => ({
        ...prev,
        latitude: lat.toString(),
        longitude: lng.toString(),
      }));
    }
  };

  const safeAppend = (
    formDataObj: FormData,
    key: string,
    value: string | number | File | null | undefined,
  ) => {
    if (value === null || value === undefined) return;

    if (value instanceof File) {
      formDataObj.append(key, value); // ✅ REAL FILE
    } else {
      formDataObj.append(key, String(value));
    }
  };

  // Helper function to safely append values (only if not null/undefined/empty)
  //   const safeAppend = (
  //     formDataObj: FormData,
  //     key: string,
  //     value: string | number | File | null | undefined,
  //   ) => {
  //     formDataObj.append(key, String(value));
  //   };

  // Transform form data to FormData for API
  const transformToFormData = (): FormData => {
    const formDataObj = new FormData();

    // Add all required fields (only if they have values)
    if (formData.businessBio?.trim()) {
      safeAppend(formDataObj, "bio", formData.businessBio.trim());
    }
    if (formData.serviceFeeMin?.trim()) {
      safeAppend(formDataObj, "service_fee_min", formData.serviceFeeMin.trim());
    }
    if (formData.serviceFeeMax?.trim()) {
      safeAppend(formDataObj, "service_fee_max", formData.serviceFeeMax.trim());
    }
    if (formData.physicalAddress?.trim()) {
      safeAppend(formDataObj, "partner_address", formData.physicalAddress.trim());
    }
    if (formData.city?.trim()) {
      safeAppend(formDataObj, "city", formData.city.trim());
    }
    if (formData.stateProvince?.trim()) {
      safeAppend(formDataObj, "state", formData.stateProvince.trim());
    }
    if (formData.zipPostalCode?.trim()) {
      safeAppend(formDataObj, "zip_code", formData.zipPostalCode.trim());
    }

    safeAppend(formDataObj, "rc_number", formData.rcNumber?.trim() || "");

    // Add is_registered (boolean)
    if (formData.isRegistered) {
      const isRegisteredValue =
        formData.isRegistered === "yes" || formData.isRegistered === "true";
      formDataObj.append("is_registered", String(isRegisteredValue));
    }
    // Add is_registered (boolean)
    if (formData.can_deliver) {
      const can_deliver =
        formData.isRegistered === "yes" || formData.can_deliver === "true";
      formDataObj.append("can_deliver", String(can_deliver));
    }
    // Add is_owner (boolean)
    if (formData.isOwner) {
      const isOwnerValue =
        formData.isOwner === "yes" || formData.isOwner === "true";
      formDataObj.append("is_owner", String(isOwnerValue));
    }
    if (formData.businessHoursOpen?.trim()) {
      safeAppend(
        formDataObj,
        "business_hours_open",
        formData.businessHoursOpen.trim(),
      );
    }
    if (formData.businessHoursClose?.trim()) {
      safeAppend(
        formDataObj,
        "business_hours_close",
        formData.businessHoursClose.trim(),
      );
    }
    if (formData.yearsOfOperation?.trim()) {
      safeAppend(
        formDataObj,
        "years_of_operation",
        formData.yearsOfOperation.trim(),
      );
    }

    // Contact Person Phone
    if (formData.businessAccountManagerPhoneNumber?.trim()) {
      const code =
        formData.businessAccountManagerPhoneCountryCode?.trim() || "";
      const number = formData.businessAccountManagerPhoneNumber.replace(
        /\D/g,
        "",
      );

      const full = code ? `${code}${number}` : number;

      safeAppend(formDataObj, "contact_person", full);
    }

    // Account Officer Phone
    if (formData.businessAccountOfficerPhoneNumber?.trim()) {
      const code =
        formData.businessAccountOfficerPhoneCountryCode?.trim() || "";
      const number = formData.businessAccountOfficerPhoneNumber.replace(
        /\D/g,
        "",
      );

      const full = code ? `${code}${number}` : number;

      safeAppend(formDataObj, "account_manager_phone", full);
    }
    // Add source_by
    if (formData.businessAccountManagerGender?.trim()) {
      safeAppend(
        formDataObj,
        "gender",
        formData.businessAccountManagerGender.trim(),
      );
    }
    // Use businessAccountManagerDesignation first, then fallback to designation
    // Always send designation (even if empty) to match AddNewPartner behavior
    const designationValue =
      formData.businessAccountManagerDesignation?.trim() ||
      formData.designation?.trim() ||
      "";
    formDataObj.append("designation", designationValue);
    if (formData.email?.trim()) {
      safeAppend(formDataObj, "email", formData.email.trim());
    }
    // Add phone number (combine country code + number)
    if (formData.phoneNumber?.trim()) {
      const phoneCountryCode = formData.phoneCountryCode?.trim() || "";
      // Remove all formatting (spaces, dashes, etc.) from phone number before saving
      const phoneNumber = formData.phoneNumber.replace(/\D/g, "");
      const fullPhoneNumber = phoneCountryCode
        ? `${phoneCountryCode}${phoneNumber}`
        : phoneNumber;
      safeAppend(formDataObj, "phone", fullPhoneNumber);
    }
    // Add country (send country code, not name)
    if (formData.country?.trim() && countryDetails?.payload?.data) {
      const countryCode = countryDetails.payload.data.find(
        (c) => c.name === formData.country,
      )?.code;
      if (countryCode) {
        safeAppend(formDataObj, "country", countryCode);
      }
    }
    if (formData.businessName?.trim()) {
      safeAppend(formDataObj, "business_name", formData.businessName.trim());
    }
    if (partnerId) {
      safeAppend(formDataObj, "partnerId", partnerId);
    }
    // Add partnership_manager (Account Officer)
    if (formData.accountManagerPartnershipManager?.trim()) {
      safeAppend(
        formDataObj,
        "partnership_manager",
        formData.accountManagerPartnershipManager.trim(),
      );
    }
    // Add source_by
    if (formData.accountManagerSourceBy?.trim()) {
      safeAppend(
        formDataObj,
        "source_by",
        formData.accountManagerSourceBy.trim(),
      );
    }

    const firstNameTrimmed =
      formData.businessAccountManagerFirstName?.trim() ?? "";
    const lastNameTrimmed =
      formData.businessAccountManagerLastName?.trim() ?? "";
    if (firstNameTrimmed) {
      safeAppend(formDataObj, "first_name", firstNameTrimmed);
    }
    if (lastNameTrimmed) {
      safeAppend(formDataObj, "last_name", lastNameTrimmed);
    }

    // Add location if address changed (latitude and longitude)
    if (formData.latitude?.trim() && formData.longitude?.trim()) {
      safeAppend(formDataObj, "latitude", formData.latitude.trim());
      safeAppend(formDataObj, "longitude", formData.longitude.trim());
      // safeAppend(
      // 	formDataObj,
      // 	"location",
      // 	`${formData.latitude.trim()},${formData.longitude.trim()}`
      // );
    }

    // Add display picture if logo is provided
    if (formData.logo) {
      safeAppend(formDataObj, "display_picture", formData.logo);
    }

    // Add display picture if logo is provided
    // if (formData.cover) {
    //   safeAppend(formDataObj, "cover_photo_url", formData.cover);
    // }

    return formDataObj;
  };

  const handleNext = () => {
    handleSubmit();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    // Validate all files
    files.forEach((file) => {
      if (!file.type.startsWith("image/")) {
        invalidFiles.push(`${file.name} - not a valid image file`);
        return;
      }
      if (file.size > maxSize) {
        invalidFiles.push(`${file.name} - size exceeds 5MB`);
        return;
      }
      validFiles.push(file);
    });

    // Show errors for invalid files
    if (invalidFiles.length > 0) {
      toast.error(
        `Some files were invalid:\n${invalidFiles.slice(0, 3).join("\n")}${
          invalidFiles.length > 3
            ? `\n...and ${invalidFiles.length - 3} more`
            : ""
        }`,
      );
    }

    if (validFiles.length === 0) return;

    setSelectedFiles(validFiles);

    // Create preview URLs for all valid files
    const previews: { file: File; url: string }[] = [];
    let loadedCount = 0;

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews.push({ file, url: reader.result as string });
        loadedCount++;
        if (loadedCount === validFiles.length) {
          setPreviewUrls(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    const hasOpenDay = Object.values(opeFormData.operational_hours).some(
      (day) => day.is_open,
    );

    console.log(hasOpenDay);

    if (!hasOpenDay) {
      setError("Business needs operating days");
      toast.error(error);
      return;
    }

    try {
      await addPartnerHours({
        id: partnerId as string,
        data: opeFormData,
      }).unwrap();

      toast.success(`Operational hours uploaded successfully`);

      // Call the optional callback if provided
      // if (onAddImage) {
      //   onAddImage();
      // }

      // Close modal and reset state
      // handleCloseModal();
    } catch (error: any) {
      toast.error(
        error?.data?.message ||
          "Failed to upload operational hours. Please try again.",
      );
    }
    setError(null);

    if (selectedFiles.length === 0) {
    } else {
      try {
        await uploadGalleryImage({
          id: partnerId as string,
          media: selectedFiles,
          is_cover: "true",
          category_name: '',
        }).unwrap();

        toast.success(
          `${selectedFiles.length} image${
            selectedFiles.length > 1 ? "s" : ""
          } uploaded successfully`,
        );

        // Call the optional callback if provided
        // if (onAddImage) {
        //   onAddImage();
        // }

        // Close modal and reset state
        // handleCloseModal();
      } catch (error: any) {
        toast.error(
          error?.data?.message || "Failed to upload images. Please try again.",
        );
      }
      setError(null);
    }

    // Validate required fields
    if (!formData.businessName.trim()) {
      toast.error("Business name is required");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!formData.phoneNumber.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (!formData.country.trim()) {
      toast.error("Country is required");
      return;
    }
    // Validate bio character limit
    if (formData.businessBio.length > 420) {
      toast.error("Business bio cannot exceed 420 characters");
      return;
    }

    try {
      const formDataObj = transformToFormData();
      console.group("🚀 Partner Update Payload");
      for (const [key, value] of formDataObj.entries()) {
        if (value instanceof File) {
          console.log(key, {
            name: value.name,
            type: value.type,
            size: `${(value.size / 1024).toFixed(2)} KB`,
            lastModified: new Date(value.lastModified).toISOString(),
          });
        } else {
          console.log(key, value);
        }
      }
      console.groupEnd();

      await updatePartner({
        partnerId: partnerId as string,
        formData: formDataObj,
      }).unwrap();

      toast.success("Partner profile updated successfully");
      // Navigate back to partner details
      setTimeout(() => {
        navigate(`/partners/details/${partnerId}`);
      }, 1000);
    } catch (err: any) {
      console.error("Failed to update partner:", err);
      const errorMessage =
        err?.data?.message ||
        err?.message ||
        err?.data?.error?.message ||
        "Failed to update partner. Please try again.";
      console.log("Error Message:", errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleBack = () => {
    navigate(`/partners/details/${partnerId}`);
  };

  if (isLoadingProfile) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <p className="text-gray-600">Loading partner profile...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <PartnerFormHeader
          title={"Edit Business Information"}
          description={"Update the business details"}
        />

        <PartnerBasicInfoForm
          showCover={true}
          opeFormData={opeFormData}
          formData={formData}
          onInputChange={handleInputChange}
          onLogoUpload={handleLogoUpload}
          logo={formData.logo}
          logoUrl={partnerProfile?.payload?.display_picture || ""}
          onCoverUpload={handleCoverUpload}
          cover={formData.cover}
          handleFileSelect={handleFileSelect}
          coverUrl={partnerProfile?.payload?.meta?.cover_photo_url || ""}
          countryDetails={countryDetails?.payload?.data || []}
          onPlaceSelect={handlePlaceSelect}
          handleOperationalHours={handleOperationalHoursChange}
        />

        <PartnerFormNavigation
          onBack={handleBack}
          onNext={handleNext}
          isLoading={isLoading}
          disabled={isLoading}
        />
      </div>
      <ToastContainer />
    </DashboardLayout>
  );
};

export default EditPartner;
